import { ArrowDownTrayIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const AUTHORING_DOWNLOAD_HREF = '/downloads/document-template-authoring-skill.zip';
const TAGGING_DOWNLOAD_HREF = '/downloads/document-token-tagging-skill.zip';
const PLUGIN_DOWNLOAD_HREF = '/downloads/document-template-authoring-plugin.zip';

function StringList({ items }: { items: string[] }) {
  return (
    <ol className="grid list-decimal gap-2 pl-5 text-sm text-muted-foreground">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );
}

function DownloadButton({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      download
      className="mt-3 inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
      {label}
    </a>
  );
}

export function ClaudeCodeSetupPage() {
  const { t } = useTranslation();

  const installSteps = t('studio.claudeCode.installSteps.steps', { returnObjects: true });

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 sm:px-6 lg:px-8">
      <div className="flex items-start gap-4">
        <div className="grid size-11 shrink-0 place-items-center rounded bg-accent text-accent-foreground ring-1 ring-border">
          <SparklesIcon className="size-5" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
            {t('studio.claudeCode.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">{t('studio.claudeCode.subtitle')}</p>
        </div>
      </div>

      <section className="mt-8 rounded-lg border border-border bg-surface p-6">
        <h2 className="text-lg font-heading font-semibold text-foreground">{t('studio.claudeCode.toolsTitle')}</h2>
        <h3 className="mt-4 text-base font-semibold text-foreground">{t('studio.claudeCode.installSteps.title')}</h3>
        <div className="mt-2">
          <StringList items={Array.isArray(installSteps) ? (installSteps as string[]) : []} />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-background p-4">
            <h3 className="text-base font-semibold text-foreground">{t('studio.claudeCode.skills.authoring.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t('studio.claudeCode.skills.authoring.body')}</p>
            <DownloadButton href={AUTHORING_DOWNLOAD_HREF} label={t('studio.claudeCode.skills.authoring.download')} />
          </div>
          <div className="rounded-lg border border-border bg-background p-4">
            <h3 className="text-base font-semibold text-foreground">{t('studio.claudeCode.skills.tagging.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t('studio.claudeCode.skills.tagging.body')}</p>
            <DownloadButton href={TAGGING_DOWNLOAD_HREF} label={t('studio.claudeCode.skills.tagging.download')} />
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">{t('studio.claudeCode.claudeCodeNote')}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('studio.claudeCode.otherToolsBody')}{' '}
          <a href={PLUGIN_DOWNLOAD_HREF} download className="font-semibold text-foreground underline underline-offset-2">
            {t('studio.claudeCode.downloadPlugin')}
          </a>
        </p>
      </section>

      <section className="mt-6 rounded-lg border border-border bg-muted p-6">
        <h2 className="text-base font-semibold text-foreground">{t('studio.claudeCode.apiKeyTitle')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t('studio.claudeCode.apiKeyBody')}</p>
      </section>

      <section className="mt-6 rounded-lg border border-border bg-surface p-6">
        <h2 className="text-lg font-heading font-semibold text-foreground">{t('studio.claudeCode.usingTitle')}</h2>
        <p className="mt-3 text-sm text-muted-foreground">{t('studio.claudeCode.usingBody')}</p>
        <p className="mt-3 text-sm text-muted-foreground">{t('studio.claudeCode.usingTaggingBody')}</p>
      </section>
    </div>
  );
}

